
import React, { useState, useEffect } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Table,
    Badge,
    InputGroup,
    Tab,
    Tabs
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEdit,
    faTrash,
    faUserShield,
    faKey,
    faSearch,
    faLock,
    faInfoCircle  
} from "@fortawesome/free-solid-svg-icons";
import { AuthUserService } from "../services/AuthUser";
import {
    AuthUser,
    Role,
    Permission,
    SecurityContext,
    ContextResource,
    UserRoleAssignment,
    UserPermissionAssignment, 
    TimezoneResponse,
    AuthUserPermissions
} from "../utils/types";
import Loading from "../utils/Loding";
import ErrorHandler from "../utils/ErrorHandler";
import { User } from "../utils/types";
import {hasPermission} from "../utils/UserHelper";

interface AuthUserPageProps {
    currentUser: User;
}

interface CreateUserFormData {
    name: string;
    email: string;
    auth0: string;
    emailVerified: boolean;
    active: number;
    timezoneId: number;
}

interface CreateRoleFormData {
    name: string;
    slug: string;
    contextTypeId: number | null;
    selectedPermissions: number[];
}

interface EditUserFormData {
    id: number;
    name: string;
    email: string;
    auth0: string;
    emailVerified: boolean;
    active: number;
    timezoneId: number;
    roles: UserRoleAssignment[];
    permissions: UserPermissionAssignment[];
}

interface AssignmentFormData {
    itemId: number;
    contextId: number | null;
    contextIds: number[];
    contextResources: ContextResource[];
    loadingContextResources: boolean;
}

interface EditRoleFormData {
    id: number;
    name: string;
    slug: string;
    contextTypeId: number | null;
    selectedPermissions: number[];
}

// Custom hook for managing auth user page state and operations
function useAuthUserManagement() {
    // State management
    const [users, setUsers] = useState<AuthUser[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [securityContexts, setSecurityContexts] = useState<SecurityContext[]>([]);
    const [contextResources, setContextResources] = useState<ContextResource[]>([]);
    const [timezones, setTimezones] = useState<TimezoneResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [roleSearchTerm, setRoleSearchTerm] = useState("");
    const [selectedContextType, setSelectedContextType] = useState<string>("");
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);  

    // Modal states
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
    const [showRolesModal, setShowRolesModal] = useState(false);
    const [showEditRoleModal, setShowEditRoleModal] = useState(false);
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
    const [showAssignPermissionModal, setShowAssignPermissionModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);

    // Form states
    const [createUserForm, setCreateUserForm] = useState<CreateUserFormData>({
        name: "",
        email: "",
        auth0: "",
        emailVerified: false,
        active: 1,
        timezoneId: 1
    });

    const [createRoleForm, setCreateRoleForm] = useState<CreateRoleFormData>({
        name: "",
        slug: "",
        contextTypeId: null,
        selectedPermissions: []
    });

    const [editRoleForm, setEditRoleForm] = useState<EditRoleFormData>({
        id: 0,
        name: "",
        slug: "",
        contextTypeId: null,
        selectedPermissions: []
    });


    const [editUserForm, setEditUserForm] = useState<EditUserFormData>({
        id: 0,
        name: "",
        email: "",
        auth0: "",
        emailVerified: false,
        active: 1,
        timezoneId: 1,
        roles: [],
        permissions: []
    });

    const [assignRoleForm, setAssignRoleForm] = useState<AssignmentFormData>({
        itemId: 0,
        contextId: null,
        contextIds: [],
        contextResources: [],
        loadingContextResources: false
    });

    const [assignPermissionForm, setAssignPermissionForm] = useState<AssignmentFormData>({
        itemId: 0,
        contextId: null,
        contextIds: [],
        contextResources: [],
        loadingContextResources: false
    });

    // Load initial data
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [usersData, rolesData, permissionsData, contextsData, timezonesData
            ] = await Promise.all([
                AuthUserService.getUsers(),
                AuthUserService.getRoles(),
                AuthUserService.getPermissions(),
                AuthUserService.getSecurityContexts(),
                AuthUserService.getTimezones()

            ]);
            setUsers(usersData.users);
            setRoles(rolesData);
            setPermissions(permissionsData);
            setSecurityContexts(contextsData);
            setTimezones(timezonesData);

        } catch (err: any) {
            const isLoggedOut = ErrorHandler.handleAuthError(err);
            if (!isLoggedOut) {
                setErrorMessage(err?.response?.data?.errorMessage || "Failed to load data");
                setShowErrorModal(true);
            }
        } finally {
            setLoading(false);
        }
    };

    const loadContextResources = async (contextType: string) => {
        try {
            const resources = await AuthUserService.getContextResources(contextType);
            setContextResources(resources);
        } catch (err: any) {
            console.error("Failed to load context resources:", err);
        }
    };


    const loadContextResourcesForAssignment = async (contextTypeId: number | null, isRole: boolean = false) => {
        if (!contextTypeId) return;
    
        const contextType = securityContexts.find(ctx => ctx.id === contextTypeId)?.slug;
        if (!contextType) return;
    
        if (isRole) {
            setAssignRoleForm(prev => ({
                ...prev,
                loadingContextResources: true,
                contextResources: [],
                contextIds: []
            }));
        } else {
            setAssignPermissionForm(prev => ({
                ...prev,
                loadingContextResources: true,
                contextResources: [],
                contextIds: []
            }));
        }
    
        try {
            const resources = await AuthUserService.getContextResources(contextType);
            if (isRole) {
                setAssignRoleForm(prev => ({
                    ...prev,
                    contextResources: resources,
                    loadingContextResources: false,
                    contextIds: []
                }));
            } else {
                setAssignPermissionForm(prev => ({
                    ...prev,
                    contextResources: resources,
                    loadingContextResources: false,
                    contextIds: []
                }));
            }
        } catch (err: any) {
            console.error("Failed to load context resources:", err);
            if (isRole) {
                setAssignRoleForm(prev => ({
                    ...prev,
                    loadingContextResources: false,
                    contextResources: []
                }));
            } else {
                setAssignPermissionForm(prev => ({
                    ...prev,
                    loadingContextResources: false,
                    contextResources: []
                }));
            }
        }
    };

    // User operations
    const handleCreateUser = async () => {
        try {
            const userData = {
                name: createUserForm.name,
                email: createUserForm.email,
                auth0: createUserForm.auth0,
                emailVerified: createUserForm.emailVerified || false,
                active: createUserForm.active,
                timezoneId: createUserForm.timezoneId
            };
            
            await AuthUserService.createUser(userData);
            setShowCreateUserModal(false);
            setCreateUserForm({
                name: "",
                email: "",
                auth0: "",
                emailVerified: false,
                active: 1,
                timezoneId: 1
            });
            await loadData();
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.errorMessage || "Failed to create user");
            setShowErrorModal(true);
        }
    };

    const handleEditUser = (user: AuthUser) => {
        setSelectedUser(user);
        setEditUserForm({
            id: user.id,
            name: user.name,
            email: user.email,
            auth0: user.auth0,
            emailVerified: user.emailVerified || false,
            active: user.active,
            timezoneId: user.timezoneId || 1,
            roles: [...user.roles],
            permissions: [...user.permissions]
        });
        setShowEditUserModal(true);
    };

    const handleUpdateUser = async () => {
        try {
            const updateData = {
                name: editUserForm.name,
                email: editUserForm.email,
                auth0: editUserForm.auth0,
                emailVerified: editUserForm.emailVerified || false,
                active: editUserForm.active,
                timezoneId: editUserForm.timezoneId,
                roles: editUserForm.roles.map(role => ({
                    roleId: role.roleId,
                    contextId: role.contextId
                })),
                permissions: editUserForm.permissions.map(permission => ({
                    permissionId: permission.permissionId,
                    contextId: permission.contextId
                }))
            };

            await AuthUserService.updateUser(editUserForm.id, updateData);
            setShowEditUserModal(false);
            setSelectedUser(null);
            await loadData();
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.errorMessage || "Failed to update user");
            setShowErrorModal(true);
        }
    };

    const handleDeleteUser = async (userId: number) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        try {
            await AuthUserService.deleteUser(userId);
            await loadData();
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.errorMessage || "Failed to delete user");
            setShowErrorModal(true);
        }
    };

    const handleCreateRole = async () => {
        try {
            const roleData = {
                name: createRoleForm.name,
                slug: createRoleForm.slug,
                contextTypeId: createRoleForm.contextTypeId,
                permissions: createRoleForm.selectedPermissions.map(id => ({ id }))
            };
            await AuthUserService.createRole(roleData);
            setShowCreateRoleModal(false);
            setCreateRoleForm({
                name: "",
                slug: "",
                contextTypeId: null,
                selectedPermissions: []
            });
            await loadData();
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.errorMessage || "Failed to create role");
            setShowErrorModal(true);
        }
    };

    const handleEditRole = (role: Role) => {
        setSelectedRole(role);
        setEditRoleForm({
            id: role.id,
            name: role.name,
            slug: role.slug,
            contextTypeId: role.contextTypeId,
            selectedPermissions: role.permissions?.map(p => p.id) || []
        });
        setShowEditRoleModal(true);
    };

    const handleUpdateRole = async () => {
        try {
            const roleData = {
                name: editRoleForm.name,
                slug: editRoleForm.slug,
                contextTypeId: editRoleForm.contextTypeId,
                permissions: editRoleForm.selectedPermissions.map(id => ({ id }))
            };
            await AuthUserService.updateRole(editRoleForm.id, roleData);
            setShowEditRoleModal(false);
            setSelectedRole(null);
            await loadData();
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.errorMessage || "Failed to update role");
            setShowErrorModal(true);
        }
    };

    const handleDeleteRole = async (roleId: number) => {
        if (!window.confirm("Are you sure you want to delete this role?")) return;
        try {
            await AuthUserService.deleteRole(roleId);
            await loadData();
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.errorMessage || "Failed to delete role");
            setShowErrorModal(true);
        }
    };

const handleAssignRoleToUser = async () => {
    if (!selectedUser || !assignRoleForm.itemId) return;
    
    try {
        const role = roles.find(r => r.id === assignRoleForm.itemId);
        if (!role) return;

        // Call the API to assign the role with context IDs
        await AuthUserService.assignRoleToUser(
            selectedUser.id, 
            assignRoleForm.itemId, 
            assignRoleForm.contextIds.length > 0 ? assignRoleForm.contextIds : null
        );

        // Create role assignments for the UI
        const newRoleAssignments: UserRoleAssignment[] = [];
        
        if (assignRoleForm.contextIds.length > 0) {
            // Create assignments for each selected context
            assignRoleForm.contextIds.forEach(contextId => {
                const contextResource = assignRoleForm.contextResources.find(r => r.id === contextId);
                newRoleAssignments.push({
                    roleId: role.id,
                    roleName: role.name,
                    roleSlug: role.slug,
                    contextId: contextId,
                    contextName: contextResource?.name || null,
                    contextIdKey: contextResource?.idKey || null
                });
            });
        } else {
            // Global assignment (no context)
            newRoleAssignments.push({
                roleId: role.id,
                roleName: role.name,
                roleSlug: role.slug,
                contextId: null,
                contextName: null,
                contextIdKey: null
            });
        }

        // Update the edit form with new role assignments
        setEditUserForm(prev => ({
            ...prev,
            roles: [...prev.roles, ...newRoleAssignments]
        }));

        // Update the main users list
        setUsers(prevUsers => 
            prevUsers.map(user => 
                user.id === selectedUser.id 
                    ? {
                        ...user,
                        roles: [...user.roles, ...newRoleAssignments]
                    }
                    : user
            )
        );

        setShowAssignRoleModal(false);
        setAssignRoleForm({
            itemId: 0,
            contextId: null,
            contextIds: [],
            contextResources: [],
            loadingContextResources: false
        });
    } catch (err: any) {
        setErrorMessage(err?.response?.data?.errorMessage || "Failed to assign role");
        setShowErrorModal(true);
    }
};

    const handleAssignPermissionToUser = async () => {
        if (!selectedUser || !assignPermissionForm.itemId) return;
        try {
        const permission = permissions.find(p => p.id === assignPermissionForm.itemId);
        if (!permission) return;
    
        // call backend (multiple requests if many contextIds)
        await AuthUserService.assignPermissionToUser(
            selectedUser.id,
            assignPermissionForm.itemId,
            assignPermissionForm.contextIds.length > 0 ? assignPermissionForm.contextIds : null
        );
    
        // prepare UI entries
        const newPermissionAssignments: UserPermissionAssignment[] = [];
        if (assignPermissionForm.contextIds.length > 0) {
            assignPermissionForm.contextIds.forEach(contextId => {
            const res = assignPermissionForm.contextResources.find(r => r.id === contextId);
            newPermissionAssignments.push({
                permissionId: permission.id,
                permissionName: permission.name,
                permissionSlug: permission.slug,
                contextId,
                contextName: res?.name || null,
                contextIdKey: res?.idKey || null
            });
            });
        } else {
            newPermissionAssignments.push({
            permissionId: permission.id,
            permissionName: permission.name,
            permissionSlug: permission.slug,
            contextId: null,
            contextName: null,
            contextIdKey: null
            });
        }
    
        setEditUserForm(prev => ({ ...prev, permissions: [...prev.permissions, ...newPermissionAssignments] }));
    
        setShowAssignPermissionModal(false);
        setAssignPermissionForm({
            itemId: 0,
            contextId: null,
            contextIds: [],
            contextResources: [],
            loadingContextResources: false
        });
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.errorMessage || "Failed to assign permission");
            setShowErrorModal(true);
        }
    };

    const handleRemoveRoleFromUser = (roleId: number, contextId: number | null) => {
        setEditUserForm(prev => ({
            ...prev,
            roles: prev.roles.filter(role =>
                !(role.roleId === roleId && role.contextId === contextId)
            )
        }));
    };

    const handleRemovePermissionFromUser = (permissionId: number, contextId: number | null) => {
        setEditUserForm(prev => ({
            ...prev,
            permissions: prev.permissions.filter(permission =>
                !(permission.permissionId === permissionId && permission.contextId === contextId)
            )
        }));
    };

    // Computed values
    const filteredUsers = users
    .slice()
    .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || "", undefined, { sensitivity: "base" }))
    .filter(user =>
      (user.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.auth0 || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredRoles = roles.filter(role =>
        role.name.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
        role.slug.toLowerCase().includes(roleSearchTerm.toLowerCase())
        
    ).sort((a, b) => a.name.localeCompare(b.name));

    return {
        // State
        users,
        roles,
        permissions,
        securityContexts,
        contextResources,
        timezones,
        loading,
        error,
        searchTerm,
        roleSearchTerm,
        selectedContextType,
        showCreateUserModal,
        showCreateRoleModal,
        showRolesModal,
        showEditRoleModal,
        showEditUserModal,
        showAssignRoleModal,
        showAssignPermissionModal,
        selectedUser,
        selectedRole,
        createUserForm,
        createRoleForm,
        editRoleForm,
        editUserForm,
        assignRoleForm,
        assignPermissionForm,
        filteredUsers,
        filteredRoles,
        showErrorModal,
        errorMessage,

        // State setters
        setError,
        setSearchTerm,
        setRoleSearchTerm,
        setSelectedContextType,
        setShowCreateUserModal,
        setShowCreateRoleModal,
        setShowRolesModal,
        setShowEditRoleModal,
        setShowEditUserModal,
        setShowAssignRoleModal,
        setShowAssignPermissionModal,
        setSelectedUser,
        setSelectedRole,
        setCreateUserForm,
        setCreateRoleForm,
        setEditRoleForm,
        setEditUserForm,
        setAssignRoleForm,
        setAssignPermissionForm,
        setShowErrorModal,
        setErrorMessage,

        // Operations
        loadContextResources,
        loadContextResourcesForAssignment,
        handleCreateUser,
        handleCreateRole,
        handleEditRole,
        handleUpdateRole,
        handleDeleteRole,
        handleEditUser,
        handleUpdateUser,
        handleDeleteUser,
        handleAssignRoleToUser,
        handleAssignPermissionToUser,
        handleRemoveRoleFromUser,
        handleRemovePermissionFromUser

    };
}

function AuthUserPage(props: AuthUserPageProps) {
    const {
        loading,
        searchTerm,
        roleSearchTerm,
        showCreateUserModal,
        showCreateRoleModal,
        showRolesModal,
        showEditRoleModal,
        showEditUserModal,
        showAssignRoleModal,
        showAssignPermissionModal,
        filteredUsers,
        filteredRoles,
        securityContexts,
        roles,
        permissions,
        timezones,
        createUserForm,
        createRoleForm,
        editRoleForm,
        editUserForm,
        assignRoleForm,
        assignPermissionForm,
        selectedUser,
        showErrorModal,
        errorMessage,
        setSearchTerm,
        setRoleSearchTerm,
        setShowCreateUserModal,
        setShowCreateRoleModal,
        setShowRolesModal,
        setShowEditRoleModal,
        setShowEditUserModal,
        setShowAssignRoleModal,
        setShowAssignPermissionModal,
        setCreateUserForm,
        setCreateRoleForm,
        setEditRoleForm,
        setEditUserForm,
        setAssignRoleForm,
        setAssignPermissionForm,
        loadContextResourcesForAssignment,
        handleCreateUser,
        handleCreateRole,
        handleEditRole,
        handleUpdateRole,
        handleDeleteRole,
        handleEditUser,
        handleUpdateUser,
        handleDeleteUser,
        handleAssignRoleToUser,
        handleAssignPermissionToUser,
        handleRemoveRoleFromUser,
        handleRemovePermissionFromUser,
        setShowErrorModal

    } = useAuthUserManagement();

    if (loading) {
        return <Loading/>;
    }

    return (
        <div className="container-fluid">
            <Row>
                <Col>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faUserShield} className="me-2"/>
                                Users
                            </h5>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="info"
                                    size="sm"
                                    onClick={() => setShowRolesModal(true)}
                                >
                                    Roles
                                </Button>
                                {hasPermission(AuthUserPermissions.userAdd) && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => setShowCreateUserModal(true)}
                                    >
                                        Create New User
                                    </Button>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body>

                            {/* Search and Filter */}
                            <Row className="mb-3">
                                <Col md={6}>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <FontAwesomeIcon icon={faSearch}/>
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search users by name, email, or Auth0 ID..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>

                            {/* Users Table */}
                            {filteredUsers.length > 0 ? (
                                <Table striped bordered hover responsive>
                                    <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Auth0 ID</th>
                                        <th>Status</th>
                                        <th>Email Verified</th>
                                        <th>Roles</th>
                                        <th>Direct Permissions</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredUsers.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.name}</td>
                                            <td>{user.email}</td>
                                            <td>{user.auth0}</td>
                                            <td>
                                                <Badge variant={user.active ? "success" : "secondary"}>
                                                    {user.active ? "Active" : "Inactive"}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge variant={user.emailVerified ? "success" : "warning"}>
                                                    {user.emailVerified ? "Verified" : "Unverified"}
                                                </Badge>
                                            </td>
                                            <td>
                                                {user.roles.length > 0 ? (
                                                    user.roles.map((role, index) => (
                                                        <Badge key={index} variant="info" className="me-1 mb-1">
                                                            {role.roleName}
                                                            {role.contextName ?
                                                                (
                                                                    <span> ({role.contextName} - {role.contextIdKey})</span>
                                                                ) : (
                                                                    <span> (Global)</span>
                                                                )}
                                                        </Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-muted">No roles</span>
                                                )}
                                            </td>
                                            <td>
                                                {user.permissions.length > 0 ? (
                                                    user.permissions.map((permission, index) => (
                                                        <Badge key={index} variant="secondary" className="me-1 mb-1">
                                                            {permission.permissionName}
                                                            {permission.contextName ?
                                                                (
                                                                    <span> ({permission.contextName} - {permission.contextIdKey})</span>
                                                                ) : (
                                                                    <span> (Global)</span>
                                                                )}
                                                        </Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-muted">No direct permissions</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {hasPermission(AuthUserPermissions.userEdit) && (
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleEditUser(user)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </Button>
                                                    )}
                                                    {hasPermission(AuthUserPermissions.userDelete) && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteUser(user.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-4">
                                    <p className="text-muted">
                                        {searchTerm ? 'No users found matching your search.' : 'No users available.'}
                                    </p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Create User Modal */}
            <Modal show={showCreateUserModal} onHide={() => setShowCreateUserModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Create New User
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={createUserForm.name}
                                        onChange={(e) => setCreateUserForm({
                                            ...createUserForm,
                                            name: e.target.value
                                        })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email *</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={createUserForm.email}
                                        onChange={(e) => setCreateUserForm({
                                            ...createUserForm,
                                            email: e.target.value
                                        })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Auth0 ID *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={createUserForm.auth0}
                                        onChange={(e) => setCreateUserForm({
                                            ...createUserForm,
                                            auth0: e.target.value
                                        })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={createUserForm.active}
                                        onChange={(e) => setCreateUserForm({
                                            ...createUserForm,
                                            active: parseInt(e.target.value)
                                        })}
                                    >
                                        <option value={1}>Active</option>
                                        <option value={0}>Inactive</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Email Verified"
                                        checked={createUserForm.emailVerified}
                                        onChange={(e) => setCreateUserForm({
                                            ...createUserForm,
                                            emailVerified: e.target.checked
                                        })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Timezone</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={createUserForm.timezoneId}
                                        onChange={(e) => setCreateUserForm({
                                            ...createUserForm,
                                            timezoneId: parseInt(e.target.value) || 1
                                        })}
                                    >
                                        {timezones.map(timezone => (
                                            <option key={timezone.id} value={timezone.id}>
                                                {timezone.code} ({timezone.zone})
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>

                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateUserModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateUser}>
                        Create User
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create Role Modal */}
            <Modal show={showCreateRoleModal} onHide={() => setShowCreateRoleModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Create New Role
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={createRoleForm.name}
                                        onChange={(e) => setCreateRoleForm({
                                            ...createRoleForm,
                                            name: e.target.value
                                        })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role Slug *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={createRoleForm.slug}
                                        onChange={(e) => setCreateRoleForm({
                                            ...createRoleForm,
                                            slug: e.target.value
                                        })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Context Type</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={createRoleForm.contextTypeId || ""}
                                        onChange={(e) => setCreateRoleForm({
                                            ...createRoleForm,
                                            contextTypeId: e.target.value ? parseInt(e.target.value) : null
                                        })}
                                    >
                                        <option value="">No Context Type</option>
                                        {securityContexts.map(context => (
                                            <option key={context.id} value={context.id}>
                                                {context.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Permissions</Form.Label>
                                    <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ced4da", padding: "10px", borderRadius: "4px" }}>
                                        {permissions.map(permission => (
                                            <Form.Check
                                                key={permission.id}
                                                type="checkbox"
                                                label={`${permission.name} (${permission.slug})`}
                                                checked={createRoleForm.selectedPermissions.includes(permission.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setCreateRoleForm({
                                                            ...createRoleForm,
                                                            selectedPermissions: [...createRoleForm.selectedPermissions, permission.id]
                                                        });
                                                    } else {
                                                        setCreateRoleForm({
                                                            ...createRoleForm,
                                                            selectedPermissions: createRoleForm.selectedPermissions.filter(id => id !== permission.id)
                                                        });
                                                    }
                                                }}
                                            />
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateRoleModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateRole}>
                        Create Role
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit User Modal */}
            <Modal show={showEditUserModal} onHide={() => setShowEditUserModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faEdit} className="me-2"/>
                        Edit User: {editUserForm.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Tabs defaultActiveKey="basic" className="mb-3">
                        <Tab eventKey="basic" title="Basic Information">
                            <Form>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Name *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={editUserForm.name}
                                                onChange={(e) => setEditUserForm({
                                                    ...editUserForm,
                                                    name: e.target.value
                                                })}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={editUserForm.email}
                                                onChange={(e) => setEditUserForm({
                                                    ...editUserForm,
                                                    email: e.target.value
                                                })}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Auth0 ID *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={editUserForm.auth0}
                                                onChange={(e) => setEditUserForm({
                                                    ...editUserForm,
                                                    auth0: e.target.value
                                                })}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Status</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={editUserForm.active}
                                                onChange={(e) => setEditUserForm({
                                                    ...editUserForm,
                                                    active: parseInt(e.target.value)
                                                })}
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                label="Email Verified"
                                                checked={editUserForm.emailVerified}
                                                onChange={(e) => setEditUserForm({
                                                    ...editUserForm,
                                                    emailVerified: e.target.checked
                                                })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Timezone</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={editUserForm.timezoneId}
                                                onChange={(e) => setEditUserForm({
                                                    ...editUserForm,
                                                    timezoneId: parseInt(e.target.value) || 1
                                                })}
                                            >
                                                {timezones.map(timezone => (
                                                    <option key={timezone.id} value={timezone.id}>
                                                        {timezone.code} ({timezone.zone})
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>

                                </Row>
                            </Form>
                        </Tab>

                        <Tab eventKey="roles" title={`Roles (${editUserForm.roles.length})`}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6>Assigned Roles</h6>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                        setAssignRoleForm({
                                            itemId: 0,
                                            contextId: null,
                                            contextIds: [],
                                            contextResources: [],
                                            loadingContextResources: false
                                        });
                                        setShowAssignRoleModal(true);
                                    }}
                                >
                                    Add Role
                                </Button>
                            </div>
                            {editUserForm.roles.length > 0 ? (
                                <Table striped bordered hover size="sm">
                                    <thead>
                                    <tr>
                                        <th>Role Name</th>
                                        <th>Context</th>
                                        <th>Context ID Key</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {editUserForm.roles.map((role, index) => (
                                        <tr key={`${role.roleId}-${role.contextId || 'null'}-${index}`}>
                                            <td>{role.roleName}</td>
                                            <td>{role.contextName || 'Global'}</td>
                                            <td>{role.contextIdKey || '-'}</td>
                                            <td>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveRoleFromUser(role.roleId, role.contextId)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-3 text-muted">
                                    No roles assigned to this user.
                                </div>
                            )}
                        </Tab>

                        <Tab eventKey="permissions" title={`Direct Permissions (${editUserForm.permissions.length})`}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6>Direct Permissions</h6>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                        setAssignPermissionForm({
                                            itemId: 0,
                                            contextId: null,
                                            contextIds: [],
                                            contextResources: [],
                                            loadingContextResources: false
                                        });
                                        setShowAssignPermissionModal(true);
                                    }}
                                >
                                    Add Permission
                                </Button>
                            </div>
                            {editUserForm.permissions.length > 0 ? (
                                <Table striped bordered hover size="sm">
                                    <thead>
                                    <tr>
                                        <th>Permission Name</th>
                                        <th>Context</th>
                                        <th>Context ID Key</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {editUserForm.permissions.map((permission, index) => (
                                        <tr key={`${permission.permissionId}-${permission.contextId || 'null'}-${index}`}>
                                            <td>{permission.permissionName}</td>
                                            <td>{permission.contextName || 'Global'}</td>
                                            <td>{permission.contextIdKey || '-'}</td>
                                            <td>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemovePermissionFromUser(permission.permissionId, permission.contextId)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-3 text-muted">
                                    No direct permissions assigned to this user.
                                </div>
                            )}
                        </Tab>
                    </Tabs>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditUserModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateUser}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Roles Management Modal */}
            <Modal show={showRolesModal} onHide={() => setShowRolesModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faLock} className="me-2"/>
                        Roles Management
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <InputGroup style={{ maxWidth: "400px" }}>
                            <InputGroup.Text>
                                <FontAwesomeIcon icon={faSearch}/>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search roles..."
                                value={roleSearchTerm}
                                onChange={(e) => setRoleSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setShowCreateRoleModal(true)}
                        >
                            Create Role
                        </Button>
                    </div>

                    {filteredRoles.length > 0 ? (
                        <Table striped bordered hover responsive>
                            <thead>
                            <tr>
                                <th>Name</th>
                                <th>Slug</th>
                                <th>Context Type</th>
                                <th>Permissions</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredRoles.map(role => (
                                <tr key={role.id}>
                                    <td>{role.name}</td>
                                    <td><code>{role.slug}</code></td>
                                    <td>
                                        {role.contextTypeId ? (
                                            securityContexts.find(ctx => ctx.id === role.contextTypeId)?.name || 'Unknown'
                                        ) : (
                                            <span className="text-muted">Global</span>
                                        )}
                                    </td>
                                    <td>
                                        {role.permissions && role.permissions.length > 0 ? (
                                            <div>
                                                {role.permissions.slice(0, 3).map((permission, index) => (
                                                    <Badge key={index} variant="secondary" className="me-1 mb-1">
                                                        {permission.name}
                                                    </Badge>
                                                ))}
                                                {role.permissions.length > 3 && (
                                                    <Badge variant="light" className="me-1 mb-1">
                                                        +{role.permissions.length - 3} more
                                                    </Badge>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-muted">No permissions</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => handleEditRole(role)}
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteRole(role.id)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-4">
                            <p className="text-muted">
                                {roleSearchTerm ? 'No roles found matching your search.' : 'No roles available.'}
                            </p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRolesModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Role Modal */}
            <Modal show={showEditRoleModal} onHide={() => setShowEditRoleModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faEdit} className="me-2"/>
                        Edit Role: {editRoleForm.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editRoleForm.name}
                                        onChange={(e) => setEditRoleForm({
                                            ...editRoleForm,
                                            name: e.target.value
                                        })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role Slug *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editRoleForm.slug}
                                        onChange={(e) => setEditRoleForm({
                                            ...editRoleForm,
                                            slug: e.target.value
                                        })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Context Type</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={editRoleForm.contextTypeId || ""}
                                        onChange={(e) => setEditRoleForm({
                                            ...editRoleForm,
                                            contextTypeId: e.target.value ? parseInt(e.target.value) : null
                                        })}
                                    >
                                        <option value="">No Context Type</option>
                                        {securityContexts.map(context => (
                                            <option key={context.id} value={context.id}>
                                                {context.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Permissions</Form.Label>
                                    <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ced4da", padding: "10px", borderRadius: "4px" }}>
                                        {permissions.map(permission => (
                                            <Form.Check
                                                key={permission.id}
                                                type="checkbox"
                                                label={`${permission.name} (${permission.slug})`}
                                                checked={editRoleForm.selectedPermissions.includes(permission.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setEditRoleForm({
                                                            ...editRoleForm,
                                                            selectedPermissions: [...editRoleForm.selectedPermissions, permission.id]
                                                        });
                                                    } else {
                                                        setEditRoleForm({
                                                            ...editRoleForm,
                                                            selectedPermissions: editRoleForm.selectedPermissions.filter(id => id !== permission.id)
                                                        });
                                                    }
                                                }}
                                            />
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditRoleModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateRole}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Assign Role Modal */}
            <Modal show={showAssignRoleModal} onHide={() => setShowAssignRoleModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faUserShield} className="me-2"/>
                        Assign Role to User
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Role *</Form.Label>
                            <Form.Control
                                as="select"
                                value={assignRoleForm.itemId}
                                onChange={(e) => {
                                    const roleId = parseInt(e.target.value);
                                    const selectedRole = roles.find(r => r.id === roleId);
                                    setAssignRoleForm(prev => ({
                                        ...prev,
                                        itemId: roleId,
                                        contextIds: [],
                                        contextResources: [],
                                        loadingContextResources: false
                                    }));
                                    
                                    if (selectedRole?.contextTypeId) {
                                        loadContextResourcesForAssignment(selectedRole.contextTypeId, true);
                                    } else {
                                        setAssignRoleForm(prev => ({
                                            ...prev,
                                            contextResources: [],
                                            contextIds: []
                                        }));
                                    }
                                }}
                                required
                            >
                                <option value={0}>Select a role...</option>
                                {roles
                                    .filter(role => {
                                        // Filter out roles already assigned to this user
                                        if (!selectedUser) return true;
                                        return !selectedUser.roles.some((userRole: UserRoleAssignment) => userRole.roleId === role.id);
                                    })
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map(role => {
                                        const contextTypeName = role.contextTypeId 
                                            ? securityContexts.find(ctx => ctx.id === role.contextTypeId)?.name || '-'
                                            : '-';
                                        return (
                                            <option key={role.id} value={role.id}>
                                                {role.name} ({contextTypeName})
                                            </option>
                                        );
                                    })}
                            </Form.Control>
                        </Form.Group>

                        {assignRoleForm.itemId > 0 && roles.find(r => r.id === assignRoleForm.itemId)?.contextTypeId && (
                            <Form.Group className="mb-3">
                                <Form.Label>Context Resources (Select multiple if needed)</Form.Label>
                                {assignRoleForm.loadingContextResources ? (
                                    <div className="text-center">Loading context resources...</div>
                                ) : (
                                    <div style={{ 
                                        maxHeight: "200px", 
                                        overflowY: "auto", 
                                        border: "1px solid #ced4da", 
                                        padding: "10px", 
                                        borderRadius: "4px",
                                        backgroundColor: "#f8f9fa"
                                    }}>
                                        {assignRoleForm.contextResources.length > 0 ? (
                                            assignRoleForm.contextResources.map(resource => (
                                                <Form.Check
                                                    key={resource.id}
                                                    type="checkbox"
                                                    id={`context-resource-${resource.id}`}
                                                    label={`${resource.name || resource.idKey} (${resource.idKey})`}
                                                    checked={assignRoleForm.contextIds.includes(resource.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setAssignRoleForm(prev => ({
                                                                ...prev,
                                                                contextIds: [...prev.contextIds, resource.id]
                                                            }));
                                                        } else {
                                                            setAssignRoleForm(prev => ({
                                                                ...prev,
                                                                contextIds: prev.contextIds.filter(id => id !== resource.id)
                                                            }));
                                                        }
                                                    }}
                                                    className="mb-2"
                                                />
                                            ))
                                        ) : (
                                            <div className="text-muted">No context resources available for this role type.</div>
                                        )}
                                    </div>
                                )}
                                {assignRoleForm.contextIds.length > 0 && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Selected: {assignRoleForm.contextIds.length} resource(s)
                                        </small>
                                    </div>
                                )}
                            </Form.Group>
                        )}

                        {assignRoleForm.itemId > 0 && !roles.find(r => r.id === assignRoleForm.itemId)?.contextTypeId && (
                            <Alert variant="info" className="mb-3">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-1"/>
                                This role has no context type. It will be assigned globally to the user.
                            </Alert>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignRoleModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAssignRoleToUser}
                        disabled={!assignRoleForm.itemId}
                    >
                        Assign Role
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Assign Permission Modal */}
            <Modal show={showAssignPermissionModal} onHide={() => setShowAssignPermissionModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faKey} className="me-2"/>
                        Assign Permission to User
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Permission *</Form.Label>
                            <Form.Control
                                as="select"
                                value={assignPermissionForm.itemId}
                                onChange={(e) => {
                                    const permissionId = parseInt(e.target.value);
                                    const selectedPermission = permissions.find(p => p.id === permissionId);
                                    setAssignPermissionForm(prev => ({
                                    ...prev,
                                    itemId: permissionId,
                                    contextIds: [],
                                    contextResources: [],
                                    loadingContextResources: false
                                    }));
                                    if (selectedPermission?.contextTypeId) {
                                    loadContextResourcesForAssignment(selectedPermission.contextTypeId, false);
                                    } else {
                                    setAssignPermissionForm(prev => ({
                                        ...prev,
                                        contextResources: [],
                                        contextIds: []
                                    }));
                                    }
                                }}
                                required
                                >
                                <option value={0}>Select a permission...</option>
                                {permissions
                                    .filter(permission => {
                                    // filter out already assigned
                                    return !editUserForm.permissions.some(up => up.permissionId === permission.id);
                                    })
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map(permission => {
                                    const contextTypeName = permission.contextTypeId
                                        ? securityContexts.find(ctx => ctx.id === permission.contextTypeId)?.name || '-'
                                        : '-';
                                    return (
                                        <option key={permission.id} value={permission.id}>
                                        {permission.name} ({contextTypeName})
                                        </option>
                                    );
                                    })}
                                </Form.Control>
                        </Form.Group>

                        {assignPermissionForm.itemId > 0 && permissions.find(p => p.id === assignPermissionForm.itemId)?.contextTypeId && (
                            <Form.Group className="mb-3">
                                <Form.Label>Context Resources (Select multiple if needed)</Form.Label>
                                {assignPermissionForm.loadingContextResources ? (
                                <div className="text-center">Loading context resources...</div>
                                ) : (
                                <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ced4da", padding: "10px", borderRadius: "4px", backgroundColor: "#f8f9fa" }}>
                                    {assignPermissionForm.contextResources.length > 0 ? (
                                    assignPermissionForm.contextResources.map(resource => (
                                        <Form.Check
                                        key={resource.id}
                                        type="checkbox"
                                        id={`perm-context-resource-${resource.id}`}
                                        label={`${resource.name || resource.idKey} (${resource.idKey})`}
                                        checked={assignPermissionForm.contextIds.includes(resource.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                            setAssignPermissionForm(prev => ({ ...prev, contextIds: [...prev.contextIds, resource.id] }));
                                            } else {
                                            setAssignPermissionForm(prev => ({ ...prev, contextIds: prev.contextIds.filter(id => id !== resource.id) }));
                                            }
                                        }}
                                        className="mb-2"
                                        />
                                    ))
                                    ) : (
                                    <div className="text-muted">No context resources available for this permission type.</div>
                                    )}
                                </div>
                                )}
                                {assignPermissionForm.contextIds.length > 0 && (
                                <div className="mt-2">
                                    <small className="text-muted">Selected: {assignPermissionForm.contextIds.length} resource(s)</small>
                                </div>
                                )}
                            </Form.Group>
                            )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignPermissionModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAssignPermissionToUser}
                        disabled={!assignPermissionForm.itemId}
                    >
                        Assign Permission
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Error Modal */}
            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">
                        Error
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <FontAwesomeIcon icon={faInfoCircle} size="3x" className="text-danger mb-3"/>
                        <p className="mb-0">{errorMessage}</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default AuthUserPage;